import { useState, useId, ChangeEvent } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ReactComponent as EyeOffIcon } from "../../../assets/eye-off.icon.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/google.icon.svg";
import { ReactComponent as MicrosoftIcon } from "../../../assets/microsoft.icon.svg";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseErrorCode } from "../../../common/enums/firebase-error-code.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { Loader, LoaderView } from "../../../components/loader/loader.comp";

const FirebaseSignIn = () => {
    const navigate = useNavigate();
    const { signIn, signInWithGoogle, signInWithMicrosoft, providerLoading } =
        FirebaseAuthContainer.useContainer();
    const [login, setLogin] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [passwordType, setPasswordType] = useState<string>("password");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const errorId = useId();
    const [searchParams] = useSearchParams();
    const invite = searchParams.get("invite");
    const registrationLink = !invite ? "/registration" : `/registration?invite=${invite}`;

    const handleLoginChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLogin(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setPassword(val ?? "");
        setErrorMessage("");
    };

    const handleEyeClick = () => {
        setPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handleSignInClick();
        }
    };

    const handleSignInClick = () => {
        if (!login || !password || isLoading || isInvalid) return;

        setIsLoading(true);
        signIn(login, password, invite)
            .catch(error => {
                switch (error.code as FirebaseErrorCode) {
                    case FirebaseErrorCode.invalidEmail:
                    case FirebaseErrorCode.invalidCredential:
                    case FirebaseErrorCode.wrongPassword:
                    case FirebaseErrorCode.userNotFound:
                        setErrorMessage(AuthErrorMessage.invalidCredential);
                        return;
                    case FirebaseErrorCode.accessPermissions:
                        navigate(
                            `/error?title=403 Error&description=${AuthErrorMessage.lackPermissions}`
                        );
                        return;
                    case FirebaseErrorCode.verificationEmailFailed:
                        setErrorMessage(AuthErrorMessage.verificationEmailFailed);
                        return;
                    default:
                        setErrorMessage(AuthErrorMessage.default);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSignInWithGoogleClick = () => {
        if (isLoading) return;

        signInWithGoogle(invite).catch(error => {
            providerErrorHandler(error);
        });
    };

    const handleSignInWithMicrosoftClick = () => {
        if (isLoading) return;

        signInWithMicrosoft(invite).catch(error => {
            providerErrorHandler(error);
        });
    };

    const providerErrorHandler = (error: any) => {
        switch (error.code as FirebaseErrorCode) {
            case FirebaseErrorCode.closePopup:
            case FirebaseErrorCode.cancelPopup:
                break;
            case FirebaseErrorCode.invalidInvite:
                setErrorMessage(AuthErrorMessage.invalidInvite);
                return;
            case FirebaseErrorCode.invalidEmail:
                setErrorMessage(AuthErrorMessage.invalidEmail);
                return;
            case FirebaseErrorCode.forbiddenInvite:
                setErrorMessage(AuthErrorMessage.forbiddenInvite);
                return;
            case FirebaseErrorCode.invalidProvider:
                setErrorMessage(AuthErrorMessage.invalidProvider);
                return;
            case FirebaseErrorCode.accessPermissions:
                navigate(`/error?title=403 Error&description=${AuthErrorMessage.lackPermissions}`);
                return;
            default:
                setErrorMessage(AuthErrorMessage.default);
        }
    };

    return (
        <AuthTemplate>
            <div className="signin">
                <div className="signin-title" role="heading" aria-level={1}>
                    Log In
                </div>
                <div className="signin-description">
                    <span className="signin-description__text">Don&apos;t have an account?</span>
                    <Link to={registrationLink} className="signin-description__link">
                        Sign Up
                    </Link>
                </div>
                <div className="signin-content">
                    <div className="signin-field">
                        <input
                            type="email"
                            aria-label="Email"
                            autoComplete="email"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Email"
                            onChange={handleLoginChange}
                            onKeyDown={handleKeyDown}
                            value={login}
                        />
                    </div>
                    <div className="signin-field">
                        <input
                            className="signin-field__input--eye"
                            type={passwordType}
                            aria-label="Password"
                            autoComplete="current-password"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Password"
                            onChange={handlePasswordChange}
                            onKeyDown={handleKeyDown}
                            value={password}
                        />
                        <button
                            type="button"
                            className="signin-field__eye"
                            aria-label={passwordType === "text" ? "Hide password" : "Show password"}
                            aria-pressed={passwordType === "text"}
                            onClick={handleEyeClick}
                        >
                            <SvgIcon
                                component={passwordType === "text" ? EyeIcon : EyeOffIcon}
                                inheritViewBox
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                    <div className="signin-messages">
                        <span id={errorId} className="signin-error" role="alert">
                            {errorMessage}
                        </span>
                        <Link className="signin-forgot" to="/forgot-password">
                            Forgot password
                        </Link>
                    </div>
                    <div className="signin-separator"></div>
                    <div className="signin-providers">
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithGoogleClick}
                            startIcon={<SvgIcon component={GoogleIcon} inheritViewBox />}
                        >
                            Continue with Google
                        </Button>
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithMicrosoftClick}
                            startIcon={<SvgIcon component={MicrosoftIcon} inheritViewBox />}
                        >
                            Continue with Microsoft
                        </Button>
                    </div>
                </div>
                <div className="signin-action">
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={handleSignInClick}
                        disabled={!login || !password || isLoading || isInvalid}
                    >
                        {!isLoading ? <span>Log in</span> : <Loader size={24} borderWidth={4} />}
                    </Button>
                </div>
                <LoaderView show={providerLoading} />
            </div>
        </AuthTemplate>
    );
};

export default FirebaseSignIn;
